// QR generation. Renders to a <canvas> (no innerHTML, no injected markup) so
// it stays clear of the CSP the same way the old data:-URL approach did.
//
// Styled to match the app: data modules are rounded dots and the three
// finder patterns ("eyes") are rounded squares, all in the app's ink color.
// The canvas background is left transparent rather than filled white, so the
// paper-colored wrapper behind it (see .qr-wrap in styles.css) shows through.

import qrcode from 'qrcode-generator';

const FINDER_SIZE = 7; // finder patterns are always a 7x7 module block

function isFinderModule(row: number, col: number, count: number): boolean {
  const inTopRows = row < FINDER_SIZE;
  const inBottomRows = row >= count - FINDER_SIZE;
  const inLeftCols = col < FINDER_SIZE;
  const inRightCols = col >= count - FINDER_SIZE;
  return (inTopRows && inLeftCols) || (inTopRows && inRightCols) || (inBottomRows && inLeftCols);
}

function roundedRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

/** Draws one finder-pattern "eye": a rounded-square ring plus a rounded pupil. */
function drawEye(ctx: CanvasRenderingContext2D, x: number, y: number, cell: number, color: string): void {
  const outer = FINDER_SIZE * cell;
  const ringWidth = cell;
  const inner = outer - ringWidth * 2;
  const pupil = 3 * cell;
  const pupilOffset = 2 * cell;

  ctx.fillStyle = color;
  ctx.beginPath();
  roundedRectPath(ctx, x, y, outer, outer, cell * 2.1);
  roundedRectPath(ctx, x + ringWidth, y + ringWidth, inner, inner, cell * 1.3);
  ctx.fill('evenodd');

  ctx.beginPath();
  roundedRectPath(ctx, x + pupilOffset, y + pupilOffset, pupil, pupil, cell * 1.2);
  ctx.fill();
}

export function qrImage(text: string, cellSize = 6, margin = 4): HTMLCanvasElement {
  const qr = qrcode(0, 'M'); // auto type, medium error correction
  qr.addData(text);
  qr.make();
  const count = qr.getModuleCount();
  const size = (count + margin * 2) * cellSize;

  const canvas = document.createElement('canvas');
  canvas.className = 'qr';
  // Backing-store resolution only (crisp on retina); the displayed box size is
  // left to CSS (.qr / .qr-wrap .qr), which keeps it square. Setting an inline
  // pixel width here would fight the stylesheet's `max-width: 100%` on `.qr`
  // (only width gets clamped, not height), stretching the QR into a rectangle.
  const ratio = window.devicePixelRatio || 1;
  canvas.width = size * ratio;
  canvas.height = size * ratio;

  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  ctx.scale(ratio, ratio);

  const color = getComputedStyle(document.documentElement).getPropertyValue('--ink').trim() || '#1b2a24';
  const offset = margin * cellSize;
  const far = offset + (count - FINDER_SIZE) * cellSize;

  drawEye(ctx, offset, offset, cellSize, color);
  drawEye(ctx, far, offset, cellSize, color);
  drawEye(ctx, offset, far, cellSize, color);

  const dotRadius = cellSize * 0.42;
  ctx.fillStyle = color;
  for (let row = 0; row < count; row++) {
    for (let col = 0; col < count; col++) {
      if (!qr.isDark(row, col) || isFinderModule(row, col, count)) continue;
      const cx = offset + col * cellSize + cellSize / 2;
      const cy = offset + row * cellSize + cellSize / 2;
      ctx.beginPath();
      ctx.arc(cx, cy, dotRadius, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  return canvas;
}
